<script lang="ts" setup>
import type { MerchantShopRenewInfoVO } from "@/api/business/types/table"
import type { RoleViewOption, RoleViewValue } from "@/store/modules/user"
import type { OrganizationScope } from "@/types/organization-scope"
import type { OrganizationScopeTreeGroup, OrganizationScopeTreeNode } from "@/utils/organizationScopeTree"
import { useStorage } from "@vueuse/core"
import dayjs from "dayjs"
import { ElLoading, ElMessage } from "element-plus"
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { getRenewInfo } from '@/api/business/table'
import { getSMSConfig } from "@/api/setting"
import { getOpenStatus } from "@/api/user/worker"
import ExpirationNoticeDialog from '@/components/dialog/ExpirationNoticeDialog.vue'
import UpdateNoticeDialog from '@/components/dialog/UpdateNoticeDialog.vue'
import Notify from "@/components/Notify/index.vue"
import Refresh from "@/components/Refresh/index.vue"
import ReservationPendingCoordinator from "@/components/ReservationPendingCoordinator.vue"
import Screenfull from "@/components/Screenfull/index.vue"
import ThemeSwitch from "@/components/ThemeSwitch/index.vue"
import { resetRouter } from "@/router"
import { isHqRoutePath, ORGANIZATION_SELECT_PATH } from "@/router/hqAccess"
import { useAppStore } from "@/store/modules/app"
import { useChainScopeStore } from "@/store/modules/chainScope"
import { usePermissionStore } from "@/store/modules/permission"
import { useSettingsStore } from "@/store/modules/settings"
import { useTagsViewStore } from "@/store/modules/tags-view"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStore } from "@/store/modules/viewScope"
import {
  refreshPermissionSession,
  switchBusinessContextWithFeedback,
} from "@/utils/businessContext/manager"
import { fetchOrganizationsForCurrentSession } from "@/utils/businessContext/organizations"
import { getToken } from "@/utils/cache/cookies"
import { isScopeSwitchableForPermissions } from "@/utils/hqMenuAccess"
import { resolveBusinessReportsCapability } from "@/utils/organizationCapabilities"
import { getOrganizationScopeStateText, resolveOrganizationScopeDescription } from "@/utils/organizationScopeSwitch"
import { buildOrganizationScopeTree } from "@/utils/organizationScopeTree"
import { resolvePermissionRefreshTarget } from "@/utils/permissionRefreshNavigation"
import { isShiftHandoverEnabled, SHIFT_HANDOVER_DISABLED_MESSAGE } from "@/utils/shiftHandoverAvailability"
import Breadcrumb from "../Breadcrumb/index.vue"
import SidebarLogo from "../Sidebar/SidebarLogo.vue"
// import Hamburger from "../Hamburger/index.vue"
import HandoverDialog from "./HandoverDialog.vue"
import OpenDialog from "./OpenDialog.vue"
import ShopInfoDialog from "./ShopInfoDialog.vue"
import StoreNavigationTools from "./StoreNavigationTools.vue"
import SystemRenewalDialog from './SystemRenewalDialog.vue'
import UserInfoDialog from "./UserInfoDialog.vue"
import VersionNotice from "./VersionNotice.vue"

// import Bookkeeping from "@/components/Bookkeeping/index.vue"
const showExpirationNotice = ref(false)
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const permissionStore = usePermissionStore()
const settingsStore = useSettingsStore()
const userStore = useUserStore()
const chainScopeStore = useChainScopeStore()
const viewScopeStore = useViewScopeStore()
const tagsViewStore = useTagsViewStore()
const isHeadquarterScope = computed(() =>
  isHqRoutePath(route.path)
  || viewScopeStore.activeScope?.type === "chain-headquarter"
  || viewScopeStore.activeScope?.type === "chain-region"
  || viewScopeStore.activeScopeId.startsWith("chain-headquarter-")
  || viewScopeStore.activeScopeId.startsWith("chain-region-"),
)
const showUserInfoDialog = ref(false)
const showHandoverDialog = ref(false)
const showHandoverOpenDialog = ref(false)
const showClose = ref(true)
const isOpen = ref<boolean>(false)
const showShopInfoDialog = ref(false)
const showSystemRenewalDialog = ref(false)
const showOrganizationDialog = ref(false)
const organizationSwitching = ref(false)
const switchingOrganizationId = ref("")
const permissionRefreshing = ref(false)
const showRoleViewDialog = ref(false)
const organizationKeyword = ref("")
const hasSms = ref(false)
const renewalItems = ref<MerchantShopRenewInfoVO[]>([])
const expiringItems = ref<MerchantShopRenewInfoVO[]>([])
const lastShowExpirationNoticeDate = useStorage('lastShowExpirationNoticeDate', '')

const user = computed(() => {
  return userStore.getUserInfo()
})

const showRoleViewSelector = computed(() => {
  return userStore.roleViewOptions.length > 0
})

type OrganizationOption = OrganizationScope & {
  code: string
  desc: string
  switchable: boolean
  stateText: string
  capabilityWarning: string
}

type OrganizationTreeOption = OrganizationScopeTreeNode<OrganizationOption>

interface RoleViewCard {
  value: RoleViewValue
  title: string
  desc: string
  tone: string
  active: boolean
}

/**
 * SLICE-002 §F2 改造:organizationOptions 数据源从 hardcoded 切到 viewScopeStore。
 * 视图模板字段(name/badge/code/desc/available)从 OrganizationScope 适配。
 * 总部/分店视图仅由后端 /me/organizations 注入。
 */
const organizationOptions = computed<OrganizationOption[]>(() =>
  viewScopeStore.organizations.map((o) => {
    const businessReports = resolveBusinessReportsCapability(o.capabilities)
    return {
      ...o,
      id: o.id,
      name: o.name,
      badge: o.badge,
      code: o.orgCode,
      desc: resolveOrganizationScopeDescription(o),
      switchable: isScopeSwitchableForPermissions(o),
      stateText: getOrganizationScopeStateText(o),
      capabilityWarning: businessReports.state === "authorized-module-not-active"
        ? `总部可进入 · ${businessReports.capability?.reason || "连锁模块未开通"}`
        : "",
    }
  }),
)

const switchableOrganizationOptions = computed(() =>
  organizationOptions.value.filter(option => option.switchable),
)

const organizationTreeGroups = computed<Array<OrganizationScopeTreeGroup<OrganizationOption>>>(() =>
  buildOrganizationScopeTree(switchableOrganizationOptions.value),
)

const activeOrganization = computed(() => {
  return organizationOptions.value.find(option => option.id === viewScopeStore.activeScopeId)
})

const canOpenOrganizationSwitcher = computed<boolean>(() => switchableOrganizationOptions.value.length > 1)

const organizationLabel = computed(() => {
  const option = activeOrganization.value
  if (!option) return "选择机构"
  return option.name
})

const matchesOrganizationKeyword = (option: OrganizationOption, keyword: string) => {
  return [option.name, option.badge, option.code, option.desc, option.capabilityWarning]
    .some(value => value.toLowerCase().includes(keyword))
}

const filteredOrganizationGroups = computed(() => {
  const keyword = organizationKeyword.value.trim().toLowerCase()
  if (!keyword) return organizationTreeGroups.value
  return organizationTreeGroups.value
    .map((group) => {
      const rootMatched = group.items.some(item => item.treeRole === "chain-root" && matchesOrganizationKeyword(item, keyword))
      if (rootMatched) return group

      const matchedIds = new Set<string>()
      const parentIds = new Set<string>()
      group.items.forEach((item) => {
        if (!matchesOrganizationKeyword(item, keyword)) return
        matchedIds.add(item.id)
        if (item.treeParentId) parentIds.add(item.treeParentId)
      })
      return {
        ...group,
        items: group.items.filter(item => matchedIds.has(item.id) || parentIds.has(item.id)),
      }
    })
    .filter(group => group.items.length > 0)
})

async function loadViewScopeOrganizationsForRefresh() {
  return fetchOrganizationsForCurrentSession({ callerHandlesAuthFailure: true })
}

const roleCardOptions = computed<RoleViewOption[]>(() => {
  if (userStore.roleViewOptions.length > 0) return userStore.roleViewOptions
  return userStore.roles
    .map<RoleViewOption | undefined>((role) => {
      if (role.merchantRoleId === undefined) return undefined
      return {
        label: role.roleName || "当前岗位",
        value: `${role.merchantRoleId}` as RoleViewValue,
        description: role.description,
      }
    })
    .filter((option): option is RoleViewOption => option !== undefined)
})

const activeRoleLabel = computed(() => {
  const activeOption = roleCardOptions.value.find(option => option.value === userStore.activeRoleView)
  if (activeOption) return activeOption.label
  if (userStore.activeRoleView === "all") return isHeadquarterScope.value ? "全部总部角色" : "全部岗位"
  return userStore.roles[0]?.roleName || "当前岗位"
})

const resolveRoleCardTone = (label: string, index: number) => {
  if (isHeadquarterScope.value) return ["owner", "admin", "supply"][index % 3]
  if (["老板", "店主", "超级管理员", "总经理"].some(keyword => label.includes(keyword))) return "owner"
  if (["系统管理员", "管理员", "店长", "经理"].some(keyword => label.includes(keyword))) return "admin"
  if (["供应链", "采购", "库存", "验货"].some(keyword => label.includes(keyword))) return "supply"
  if (["财务", "股东"].some(keyword => label.includes(keyword))) return "finance"
  return ["owner", "admin", "supply"][index % 3]
}

const resolveRoleCardTitle = (option: RoleViewOption) => {
  return option.value === "all" ? option.label : `我是${option.label}`
}

const resolveRoleCardDesc = (option: RoleViewOption) => {
  const label = option.label
  if (isHeadquarterScope.value) {
    if (option.value === "all") return "使用当前账号全部总部角色权限"
    return option.description?.trim() || "仅使用该总部角色的权限"
  }
  if (option.value === "all") return "使用当前账号全部岗位权限"
  if (["老板", "店主", "超级管理员", "总经理"].some(keyword => label.includes(keyword))) {
    return "我是老板，关注数据做决策"
  }
  if (["系统管理员", "管理员", "店长", "经理"].some(keyword => label.includes(keyword))) {
    return "我负责系统配置，经营管理很在行"
  }
  if (["供应链", "采购", "库存", "验货"].some(keyword => label.includes(keyword))) {
    return "库存管理、采购、验货样样精通"
  }
  if (["财务", "股东"].some(keyword => label.includes(keyword))) {
    return "关注财务、报表和经营分析"
  }
  if (["收银", "服务员", "营业"].some(keyword => label.includes(keyword))) {
    return "处理营业收银和门店服务"
  }
  return "仅使用该岗位权限组织首页入口"
}

const roleViewCards = computed<RoleViewCard[]>(() => {
  return roleCardOptions.value.map((option, index) => ({
    value: option.value,
    title: resolveRoleCardTitle(option),
    desc: resolveRoleCardDesc(option),
    tone: resolveRoleCardTone(option.label, index),
    active: option.value === userStore.activeRoleView,
  }))
})

const showUpdateNoticeDialog = computed(() => {
  return appStore.showUpdateNoticeDialog
})

const showUpdateNoticeDialogMode = computed(() => {
  return appStore.showUpdateNoticeDialogMode
})

// const sidebar = computed(() => {
//   return appStore.sidebar
// })
const showNotify = computed(() => {
  return settingsStore.showNotify
})
const showThemeSwitch = computed(() => {
  return settingsStore.showThemeSwitch
})
const showScreenfull = computed(() => {
  return settingsStore.showScreenfull
})

const shiftHandoverEnabled = computed(() => isShiftHandoverEnabled(userStore.getShopInfo().shiftHandoverStatus))

const handleUserInfo = () => {
  showUserInfoDialog.value = true
}

const redirectIfCurrentRouteForbidden = async () => {
  const currentRoute = router.currentRoute.value
  const firstRoute = permissionStore.getFirstAccessibleRoute()
  const target = resolvePermissionRefreshTarget({
    currentPath: currentRoute.path,
    currentFullPath: currentRoute.fullPath,
    currentRouteAccessible: permissionStore.canAccessRoute(currentRoute),
    firstAccessibleRouteName: firstRoute?.name,
    replayAccessibleCurrentRoute: false,
  })
  if (target) {
    await router.replace(target)
  }
}

const refreshPermissionState = async () => {
  if (permissionRefreshing.value) return
  permissionRefreshing.value = true
  const loading = ElLoading.service({
    lock: true,
    text: "正在刷新权限状态...",
    background: "rgba(255, 255, 255, 0.72)",
  })
  try {
    const result = await refreshPermissionSession("navigation-menu", {
      getToken,
      router,
      resetRouter,
      loadOrganizations: loadViewScopeOrganizationsForRefresh,
      viewScopeStore,
      userStore,
      permissionStore,
      tagsViewStore,
    })
    if (!result.applied) {
      if (result.discardReason === "scope-changed" || result.discardReason === "no-allowed-scope") {
        await router.replace({
          path: ORGANIZATION_SELECT_PATH,
          query: { redirect: route.fullPath },
        })
        return
      }
      ElMessage.warning("权限状态已变化，请稍后重试")
      return
    }
    await redirectIfCurrentRouteForbidden()
    ElMessage.success("权限状态已刷新")
  } catch (_error) {
    ElMessage.error("权限状态同步失败，请稍后重试")
  } finally {
    loading.close()
    permissionRefreshing.value = false
  }
}

const handlePreCheck = () => {
  if (!shiftHandoverEnabled.value) {
    ElMessage.warning(SHIFT_HANDOVER_DISABLED_MESSAGE)
    return false
  }
  return true
}

const handleOpen = () => {
  if (!handlePreCheck()) {
    return
  }
  if (!userStore.checkPermission("business_worker_handover")) {
    ElMessage.error("您没有权限开班")
    return
  }
  isOpen.value = userStore.isOpen
  if (isOpen.value) {
    ElMessage.warning('当前已开班')
    return
  }
  showHandoverOpenDialog.value = true
}

const handleHandover = () => {
  if (!handlePreCheck()) {
    return
  }
  if (!userStore.checkPermission("business_worker_handover")) {
    ElMessage.error("您没有权限交接班")
    return
  }
  isOpen.value = userStore.isOpen
  if (!isOpen.value) {
    ElMessage({
      message: "当前未开班, 请先开班",
      type: "warning",
    })
    return
  }
  // showHandoverDialog.value = true
  router.push({
    name: 'HandoverLayout',
  })
}

const handleHandoverHistory = () => {
  if (!handlePreCheck()) {
    return
  }
  if (!userStore.checkPermission("business_worker_detail")) {
    ElMessage.error("您没有权限查看交接班记录")
    return
  }
  router.push("/handover/history")
}

// const toggleSidebar = () => {
//   appStore.toggleSidebar(false)
// }

onMounted(() => {
  appStore.toggleSidebar(true)
})

const logout = () => {
  userStore.logout()
  router.push("/login")
}

const getOpenStatusByShopId = () => {
  if (isHeadquarterScope.value) return
  getOpenStatus().then((res) => {
    isOpen.value = res.data
    userStore.setOpen(isOpen.value)
    if (!isOpen.value && shiftHandoverEnabled.value && userStore.checkPermission("business_worker_handover")) {
      showHandoverOpenDialog.value = true
      showClose.value = false
    }
  })
}

const handleShopInfoClick = () => {
  showShopInfoDialog.value = true
}

const openOrganizationDialog = () => {
  showOrganizationDialog.value = true
}

const openRoleViewDialog = () => {
  if (!showRoleViewSelector.value) {
    ElMessage.info("当前账号暂无可切换岗位")
    return
  }
  showRoleViewDialog.value = true
}

const handleOrganizationSelect = async (option: OrganizationTreeOption) => {
  if (!option.switchable) {
    ElMessage.warning(option.stateText)
    return
  }
  if (organizationSwitching.value) return
  organizationSwitching.value = true
  switchingOrganizationId.value = option.id
  try {
    const result = await switchBusinessContextWithFeedback(
      {
        scopeId: option.id,
        route,
        router,
        viewScopeStore,
        chainScopeStore,
        userStore,
        permissionStore,
        tagsViewStore,
        resetRouter,
      },
      {
        loadingText: `正在切换至${option.name}，刷新店铺数据和菜单...`,
        openLoading: text => ElLoading.service({
          lock: true,
          text,
          background: "rgba(255, 255, 255, 0.72)",
        }),
      },
    )
    showOrganizationDialog.value = false
    if (result.changed) {
      ElMessage.success(`已切换至${option.name}`)
    }
  } catch (error) {
    console.warn("机构切换失败:", error)
    ElMessage.error({
      message: "机构切换失败，请稍后重试",
      grouping: true,
    })
  } finally {
    organizationSwitching.value = false
    switchingOrganizationId.value = ""
  }
}

const handleRoleViewChange = async (value: RoleViewValue) => {
  await userStore.changeRoles(value)
  const currentRoute = route.matched[route.matched.length - 1]
  const canAccessCurrentRoute = currentRoute?.name ? permissionStore.hasRouteName(currentRoute.name) : true
  const firstRoute = permissionStore.getFirstAccessibleRoute()
  if (!canAccessCurrentRoute && firstRoute) {
    router.replace({ name: firstRoute.name })
  }
}

const handleRoleCardSelect = async (card: RoleViewCard) => {
  if (card.active) return
  try {
    await handleRoleViewChange(card.value)
    showRoleViewDialog.value = false
    ElMessage.success(`已切换至${card.title.replace(/^我是/, "")}`)
  } catch (_error) {
    ElMessage.error("岗位切换失败，请稍后重试")
  }
}

const handleSystemRenewal = () => {
  showSystemRenewalDialog.value = true
}

function checkExpiringItems() {
  // 如果没有开通短信，则不需要判断短信的条数
  expiringItems.value = renewalItems.value.filter(item => {
    if ([1, 2].includes(item.productType)) {
      const daysRemaining = dayjs(item.expirationDate).diff(dayjs(), 'days')
      return daysRemaining <= 7
    } else if (item.productType === 4 && hasSms.value) {
      return item.messageNumber <= 100
    }
    return false
  })

  if (expiringItems.value.length > 0) {
    showExpirationNotice.value = true
  }
}

// 获取续费信息
async function fetchRenewInfo() {
  if (isHeadquarterScope.value) return
  try {
    const { data } = await getRenewInfo()
    if (data) {
      renewalItems.value = data
    }
  } catch (error) {
    console.error('获取续费信息失败:', error)
  }
}

async function fetchSmsConfig() {
  if (isHeadquarterScope.value) return
  try {
    const { data } = await getSMSConfig()
    hasSms.value = data.status === 1
  } catch (error) {
    console.error('获取短信配置失败:', error)
  }
}

// 判断今天是否已经弹窗
if (!lastShowExpirationNoticeDate.value || !dayjs(lastShowExpirationNoticeDate.value).isSame(dayjs(), 'day')) {
  Promise.all([fetchRenewInfo(), fetchSmsConfig()]).then(() => {
    checkExpiringItems()
    lastShowExpirationNoticeDate.value = dayjs().format('YYYY-MM-DD')
  })
}

getOpenStatusByShopId()
</script>

<template>
  <div class="navigation-bar">
    <!-- <Hamburger :is-active="sidebar.opened" class="hamburger" @toggle-click="toggleSidebar" /> -->
    <div class="nav-logo">
      <SidebarLogo :collapse="false" />
    </div>
    <Breadcrumb class="breadcrumb" />
    <VersionNotice class="version-notice" />
    <ReservationPendingCoordinator />
    <div class="right-menu">
      <slot name="assistant" />
      <!-- <Bookkeeping class="right-menu-item" /> -->
      <ThemeSwitch v-if="showThemeSwitch" class="right-menu-item" />
      <Refresh class="right-menu-item" />
      <Screenfull v-if="showScreenfull" class="right-menu-item" />
      <StoreNavigationTools v-if="!isHeadquarterScope" />
      <Notify v-if="showNotify" class="right-menu-item" />
      <div class="right-menu-item business-context-switcher">
        <!-- 多个真实可进入机构才显示 dropdown -->
        <template v-if="canOpenOrganizationSwitcher">
          <button
            type="button"
            class="business-store-switch"
            :title="organizationLabel"
            @click="openOrganizationDialog"
          >
            <span class="business-switch__name">{{ organizationLabel }}</span>
            <el-icon class="business-switch__caret"><CaretBottom /></el-icon>
          </button>
        </template>
        <template v-else>
          <span class="business-store-static" :title="organizationLabel">
            <span class="business-switch__name">{{ organizationLabel }}</span>
          </span>
        </template>
        <button
          type="button"
          class="business-role-switch"
          :class="{ disabled: !showRoleViewSelector }"
          :title="activeRoleLabel"
          @click="openRoleViewDialog"
        >
          <span class="business-switch__name">{{ activeRoleLabel }}</span>
          <el-icon class="business-switch__caret"><CaretBottom /></el-icon>
        </button>
      </div>

      <el-dropdown :trigger="$mediaInfo.isMobile ? 'click' : 'hover'" class="right-menu-item">
        <div class="flex flex-nowrap">
          <el-icon :size="20"><User /></el-icon>
          <div flex items-center>
            <span ml-4px>{{ user?.name }}</span>
            <el-icon ml-4px><CaretBottom /></el-icon>
          </div>
        </div>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item v-if="!isHeadquarterScope" :disabled="!shiftHandoverEnabled" @click="handleOpen">
              开班
            </el-dropdown-item>
            <el-dropdown-item v-if="!isHeadquarterScope" :disabled="!shiftHandoverEnabled" @click="handleHandover">
              交接班
            </el-dropdown-item>
            <el-dropdown-item v-if="!isHeadquarterScope" :disabled="!shiftHandoverEnabled" @click="handleHandoverHistory">
              交班记录
            </el-dropdown-item>
            <el-dropdown-item v-if="!isHeadquarterScope" @click="handleShopInfoClick">
              门店信息
            </el-dropdown-item>
            <el-dropdown-item v-if="!isHeadquarterScope" @click="handleSystemRenewal">
              续费管理
            </el-dropdown-item>
            <el-dropdown-item @click="handleUserInfo">
              账号信息
            </el-dropdown-item>
            <el-dropdown-item :disabled="permissionRefreshing" @click="refreshPermissionState">
              刷新权限状态
            </el-dropdown-item>
            <el-dropdown-item @click="logout" style="color: #D54941">
              退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <el-dialog
      v-model="showOrganizationDialog"
      :append-to-body="true"
      width="560px"
      title="切换机构视图"
      class="organization-switch-dialog"
    >
      <div class="organization-switch">
        <el-input
          v-model="organizationKeyword"
          clearable
          placeholder="搜索机构名称 / 编码"
          class="organization-search"
        />
        <div class="organization-list" role="tree">
          <div
            v-for="group in filteredOrganizationGroups"
            :key="group.key"
            class="organization-tree-group"
          >
            <div class="organization-tree-group__title">{{ group.title }}</div>
            <div class="organization-tree-group__desc">{{ group.desc }}</div>
            <button
              v-for="option in group.items"
              :key="option.id"
              type="button"
              role="treeitem"
              class="organization-card"
              :class="{
                active: option.id === activeOrganization?.id,
                child: option.treeDepth > 0,
                disabled: !option.switchable || organizationSwitching,
                switching: option.id === switchingOrganizationId,
              }"
              :disabled="organizationSwitching || !option.switchable"
              :aria-level="option.treeDepth + 1"
              :aria-busy="option.id === switchingOrganizationId ? 'true' : undefined"
              @click="handleOrganizationSelect(option)"
            >
              <span v-if="option.treeDepth > 0" class="organization-card__branch" />
              <span class="organization-card__main">
                <span class="organization-card__title">
                  <span>{{ option.name }}</span>
                  <span class="organization-card__badge">{{ option.badge }}</span>
                  <span v-if="option.treeChildrenCount > 0" class="organization-card__children">{{ option.treeChildrenCount }} 家分店</span>
                </span>
                <span class="organization-card__meta">机构编码：{{ option.code }}</span>
                <span class="organization-card__desc">{{ option.desc }}</span>
                <span v-if="option.capabilityWarning" class="organization-card__capability-warning">
                  {{ option.capabilityWarning }}
                </span>
              </span>
              <span v-if="option.id === switchingOrganizationId" class="organization-card__state switching">切换中...</span>
              <span v-else-if="option.id === activeOrganization?.id" class="organization-card__state">当前</span>
              <span v-else-if="!option.switchable" class="organization-card__state muted">{{ option.stateText }}</span>
            </button>
          </div>
        </div>
      </div>
    </el-dialog>
    <el-dialog
      v-model="showRoleViewDialog"
      :append-to-body="true"
      width="640px"
      :title="isHeadquarterScope ? '总部角色切换' : '岗位切换'"
      class="role-view-switch-dialog"
    >
      <div class="role-view-switch">
        <button
          v-for="card in roleViewCards"
          :key="card.value"
          type="button"
          class="role-view-card"
          :class="[`role-view-card--${card.tone}`, { active: card.active }]"
          @click="handleRoleCardSelect(card)"
        >
          <span class="role-view-card__figure" aria-hidden="true">
            <span class="role-view-card__head" />
            <span class="role-view-card__body" />
          </span>
          <span class="role-view-card__content">
            <span class="role-view-card__title">{{ card.title }}</span>
            <span class="role-view-card__desc">{{ card.desc }}</span>
          </span>
          <span v-if="card.active" class="role-view-card__ribbon">当前</span>
        </button>
      </div>
    </el-dialog>
    <UserInfoDialog v-if="showUserInfoDialog" :show="showUserInfoDialog" @update:show="showUserInfoDialog = $event" />
    <HandoverDialog
      v-if="showHandoverDialog"
      :show="showHandoverDialog"
      @refresh="getOpenStatusByShopId"
      @update:show="showHandoverDialog = $event"
    />
    <OpenDialog
      v-if="showHandoverOpenDialog"
      :show="showHandoverOpenDialog"
      :showClose="showClose"
      @refresh="getOpenStatusByShopId"
      @update:show="showHandoverOpenDialog = $event"
    />
    <ShopInfoDialog
      v-if="showShopInfoDialog"
      v-model="showShopInfoDialog"
    />
    <SystemRenewalDialog
      v-if="showSystemRenewalDialog"
      v-model:show="showSystemRenewalDialog"
    />
    <!-- 即将到期通知 -->
    <ExpirationNoticeDialog
      v-model:show="showExpirationNotice"
      :expiring-items="expiringItems"
    />
    <UpdateNoticeDialog
      v-if="showUpdateNoticeDialog"
      v-model="showUpdateNoticeDialog"
      :mode="showUpdateNoticeDialogMode"
    />
  </div>
</template>

<style lang="scss" scoped>
.navigation-bar {
  display: flex;
  height: var(--v3-navigationbar-height);
  padding-right: 24px;
  overflow: hidden;
  background: transparent;

  .nav-logo {
    float: left;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    width: var(--v3-sidebar-width);
    height: 100%;
    padding: 0;

    :deep(.sidebar-logo-container) {
      --logo-text-align: left;

      text-align: left;
    }
  }

  .hamburger {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 15px;
    margin-left: 0;
    cursor: pointer;
  }

  .version-notice {
    flex: 1 1 200px;
  }

  .breadcrumb {
    margin-left: 10px;

    // 参考 Bootstrap 的响应式设计 WIDTH = 576
    @media screen and (width <= 576px) {
      display: none;
    }
  }

  .right-menu {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: 100%;
    margin-right: 10px;
    margin-left: auto;
    color: var(--bussiness-sidebar-text-color);

    .right-menu-item {
      display: flex;
      align-items: center;
      padding: 0 10px;
      color: var(--bussiness-sidebar-text-color);
      cursor: pointer;
    }

    .shop-info {
      display: flex;
      align-items: center;
      padding: 0 10px;
      cursor: pointer;

      .el-icon {
        margin-right: 8px;
        font-size: 20px;
        color: var(--bussiness-sidebar-text-color);
      }
    }

    .business-context-switcher {
      gap: 8px;
      padding: 0 8px;
      cursor: default;
    }

    .business-store-switch,
    .business-role-switch {
      display: flex;
      gap: 4px;
      align-items: center;
      height: 100%;
      padding: 0;
      font: inherit;
      color: inherit;
      cursor: pointer;
      outline: none;
      background: transparent;
      border: 0;
    }

    .business-store-static {
      display: flex;
      gap: 4px;
      align-items: center;
      height: 100%;
      font: inherit;
      color: inherit;
      cursor: default;
    }

    .business-store-switch:focus-visible,
    .business-role-switch:focus-visible {
      box-shadow: 0 0 0 3px #0052d933;
    }

    .business-switch__name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .business-store-switch .business-switch__name {
      max-width: 180px;
    }

    .business-role-switch .business-switch__name {
      max-width: 96px;
    }

    .business-switch__caret {
      flex-shrink: 0;
    }

    .business-role-switch {
      justify-content: center;
    }

    .business-role-switch.disabled {
      cursor: default;
    }
  }
}

:deep(.organization-switch-dialog) {
  .el-dialog__body {
    padding-top: 4px;
  }
}

.organization-switch {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.organization-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 420px;
  overflow: auto;
}

.organization-tree-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.organization-tree-group__title {
  padding: 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.organization-tree-group__desc {
  padding: 0 2px 2px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.organization-card {
  position: relative;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  font: inherit;
  color: var(--el-text-color-primary);
  text-align: left;
  cursor: pointer;
  background: var(--el-fill-color-lighter);
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
}

.organization-card.child {
  width: calc(100% - 32px);
  margin-left: 32px;
}

.organization-card__branch {
  position: absolute;
  top: -8px;
  bottom: 50%;
  left: -18px;
  width: 18px;
  border-bottom: 1px solid var(--el-border-color);
  border-left: 1px solid var(--el-border-color);
  border-bottom-left-radius: 6px;
}

.organization-card.active {
  background: var(--tea-color-primary-subtle);
  border-color: var(--tea-color-primary);
  box-shadow: 0 8px 20px rgb(var(--tea-color-primary-rgb) / 14%);
}

.organization-card.disabled {
  cursor: default;
  opacity: 0.66;
}

.organization-card.switching {
  border-color: var(--tea-color-primary);
  box-shadow: 0 8px 20px rgb(var(--tea-color-primary-rgb) / 14%);
  opacity: 1;
}

.organization-card__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.organization-card__title {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
}

.organization-card__title span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.organization-card__badge {
  flex-shrink: 0;
  padding: 2px 6px;
  font-size: 12px;
  color: var(--tea-color-primary);
  background: var(--tea-color-primary-subtle);
  border: 1px solid var(--tea-color-primary-light-7);
  border-radius: 4px;
}

.organization-card__children {
  flex-shrink: 0;
  padding: 2px 6px;
  font-size: 12px;
  color: var(--el-text-color-regular);
  background: var(--el-fill-color);
  border-radius: 4px;
}

.organization-card__meta,
.organization-card__desc {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 1.4;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.organization-card__capability-warning {
  font-size: 12px;
  line-height: 1.4;
  color: var(--el-color-warning-dark-2);
}

.organization-card__state {
  flex-shrink: 0;
  padding: 3px 8px;
  font-size: 12px;
  color: var(--tea-color-primary);
  background: var(--tea-color-primary-subtle);
  border-radius: 999px;
}

.organization-card__state.muted {
  color: var(--el-text-color-secondary);
  background: var(--el-fill-color);
}

.organization-card__state.switching {
  color: #fff;
  background: var(--tea-color-primary);
}

:deep(.role-view-switch-dialog) {
  .el-dialog__body {
    padding-top: 4px;
  }
}

.role-view-switch {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.role-view-card {
  position: relative;
  display: flex;
  gap: 20px;
  align-items: center;
  width: 100%;
  min-height: 104px;
  padding: 18px 24px;
  overflow: hidden;
  font: inherit;
  color: var(--el-text-color-primary);
  text-align: left;
  cursor: pointer;
  background: linear-gradient(
    135deg,
    var(--tea-color-primary-light-9) 0%,
    var(--tea-color-primary-light-8) 100%
  );
  border: 1px solid transparent;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgb(var(--tea-color-primary-rgb) / 12%);
}

.role-view-card--admin {
  background: linear-gradient(
    135deg,
    var(--tea-color-primary-light-9) 0%,
    var(--tea-color-primary-light-7) 100%
  );
}

.role-view-card--supply {
  background: linear-gradient(
    135deg,
    var(--tea-color-primary-light-9) 0%,
    var(--tea-color-primary-light-8) 100%
  );
}

.role-view-card--finance {
  background: linear-gradient(
    135deg,
    var(--tea-color-primary-light-9) 0%,
    var(--tea-color-primary-light-8) 100%
  );
}

.role-view-card.active {
  border-color: var(--tea-color-primary);
  box-shadow: 0 10px 26px rgb(var(--tea-color-primary-rgb) / 22%);
}

.role-view-card__figure {
  position: relative;
  flex-shrink: 0;
  width: 92px;
  height: 68px;
}

.role-view-card__head {
  position: absolute;
  top: 0;
  left: 30px;
  width: 34px;
  height: 34px;
  background: rgb(var(--tea-color-primary-rgb) / 28%);
  border-radius: 50%;
}

.role-view-card__body {
  position: absolute;
  right: 12px;
  bottom: -20px;
  left: 12px;
  height: 62px;
  background: rgb(var(--tea-color-primary-rgb) / 36%);
  border-radius: 999px 999px 8px 8px;
}

.role-view-card--admin .role-view-card__head,
.role-view-card--admin .role-view-card__body {
  background: rgb(var(--tea-color-primary-rgb) / 30%);
}

.role-view-card--supply .role-view-card__head,
.role-view-card--supply .role-view-card__body {
  background: rgb(var(--tea-color-primary-rgb) / 28%);
}

.role-view-card--finance .role-view-card__head,
.role-view-card--finance .role-view-card__body {
  background: rgb(var(--tea-color-primary-rgb) / 30%);
}

.role-view-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.role-view-card__title {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
}

.role-view-card__desc {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  line-height: 1.4;
  color: var(--tea-color-text-regular);
  white-space: nowrap;
}

.role-view-card__ribbon {
  position: absolute;
  top: 11px;
  right: -26px;
  width: 92px;
  padding: 3px 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--tea-color-on-primary);
  text-align: center;
  background: var(--tea-color-primary);
  transform: rotate(45deg);
  transform-origin: center;
}
</style>
